import { CheckIcon, ChevronDownIcon, XIcon } from "@heroicons/react/outline";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "@refinedev/core";
import { Fragment, ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";

type LanguageCode = "vi" | "en";
type CurrencyCode = "vnd" | "usd";

type LanguageGroupProps = {
  className?: string;
  showCurrency?: boolean;
  onLanguageChange?: (language: LanguageCode) => void;
  onCurrencyChange?: (currency: CurrencyCode) => void;
};

type DialogLanguageProps = {
  open: boolean;
  showCurrency: boolean;
  draftCurrency: CurrencyCode;
  draftLanguage: LanguageCode;
  onClose: () => void;
  onApply: () => void;
  setDraftCurrency: (currency: CurrencyCode) => void;
  setDraftLanguage: (language: LanguageCode) => void;
  translate: (key: string) => string;
};

const LANGUAGE_OPTIONS: Array<{
  code: LanguageCode;
  name: string;
  shortName: string;
  icon: string;
}> = [
  {
    code: "vi",
    name: "Tiếng Việt",
    shortName: "VI",
    icon: "/images/flags/circle-vi.png",
  },
  {
    code: "en",
    name: "English",
    shortName: "EN",
    icon: "/images/flags/circle-en.png",
  },
];

const CURRENCY_OPTIONS: Array<{
  code: CurrencyCode;
  name: string;
  shortName: string;
}> = [
  {
    code: "vnd",
    name: "Vietnam Dong",
    shortName: "VND",
  },
  {
    code: "usd",
    name: "United States Dollar ($)",
    shortName: "USD",
  },
];

const isLanguageCode = (value?: string | null): value is LanguageCode => {
  return value === "vi" || value === "en";
};

const isCurrencyCode = (value?: string | null): value is CurrencyCode => {
  return value === "vnd" || value === "usd";
};

const getStoredLanguage = (): LanguageCode => {
  const storedLanguage = localStorage.getItem("lang");

  if (isLanguageCode(storedLanguage)) {
    return storedLanguage;
  }

  localStorage.setItem("lang", "vi");
  return "vi";
};

const getStoredCurrency = (): CurrencyCode => {
  const storedCurrency = localStorage.getItem("wsCurrency");

  if (isCurrencyCode(storedCurrency)) {
    return storedCurrency;
  }

  const legacyCurrency = localStorage.getItem("currency")?.toLowerCase();

  if (isCurrencyCode(legacyCurrency)) {
    localStorage.setItem("wsCurrency", legacyCurrency);
    return legacyCurrency;
  }

  localStorage.setItem("wsCurrency", "vnd");
  return "vnd";
};

const OptionRow = ({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      className={twMerge(
        "flex h-11 w-full items-center justify-between rounded-md px-3 text-left transition-colors",
        active
          ? "border border-[#011BB6] bg-[#F4F6FF] text-[#011BB6]"
          : "border border-transparent text-[#262626] hover:bg-[#F5F6F7]"
      )}
      onClick={onClick}
    >
      {children}
      {active && <CheckIcon className="size-[16px] shrink-0" strokeWidth={2} />}
    </button>
  );
};

const DialogLanguage = ({
  open,
  showCurrency,
  draftCurrency,
  draftLanguage,
  onClose,
  onApply,
  setDraftCurrency,
  setDraftLanguage,
  translate,
}: DialogLanguageProps) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-3 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-3 sm:scale-95"
            >
              <Dialog.Panel
                className={twMerge(
                  "relative w-full max-w-md transform rounded-lg bg-white p-4 text-left shadow-xl transition-all sm:max-w-xl sm:p-5 lg:max-w-2xl"
                )}
              >
                <div className="mb-5 flex items-center justify-between">
                  <Dialog.Title className="text-16 font-700 text-[#262626]">
                    {translate("languages")}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="flex size-8 items-center justify-center rounded-md text-[#3D3D3D] hover:bg-[#F5F6F7]"
                    onClick={onClose}
                  >
                    <XIcon className="size-[18px]" strokeWidth={3} />
                  </button>
                </div>

                <div
                  className={twMerge(
                    "grid grid-cols-1 gap-5",
                    showCurrency && "md:grid-cols-2 md:gap-6"
                  )}
                >
                  <div>
                    {showCurrency && (
                      <div className="mb-3 text-14 font-700 text-[#3D3D3D]">
                        {translate("languages")}
                      </div>
                    )}
                    <div className="grid gap-2">
                      {LANGUAGE_OPTIONS.map((language) => (
                        <OptionRow
                          key={language.code}
                          active={draftLanguage === language.code}
                          onClick={() => setDraftLanguage(language.code)}
                        >
                          <span className="flex items-center gap-x-2">
                            <img
                              loading="lazy"
                              src={language.icon}
                              className="size-[22px] rounded-full"
                              alt={language.name}
                            />
                            <span className="text-14 font-600">
                              {language.name}
                            </span>
                          </span>
                        </OptionRow>
                      ))}
                    </div>
                  </div>

                  {showCurrency && (
                    <div className="md:border-l md:border-[#E5E7EB] md:pl-6">
                      <div className="mb-1 text-14 font-700 text-[#3D3D3D]">
                        {translate("currencies")}
                      </div>
                      <div className="mb-3 text-12 italic text-[#6B7280]">
                        {translate("change_currencies_content")}
                      </div>
                      <div className="grid gap-2">
                        {CURRENCY_OPTIONS.map((currency) => (
                          <OptionRow
                            key={currency.code}
                            active={draftCurrency === currency.code}
                            onClick={() => setDraftCurrency(currency.code)}
                          >
                            <span className="flex items-center gap-x-2 text-14">
                              <span className="font-700">
                                {currency.shortName}
                              </span>
                              <span className="text-[#6B7280]">-</span>
                              <span className="font-500">{currency.name}</span>
                            </span>
                          </OptionRow>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-x-3">
                  <button
                    type="button"
                    className="flex h-10 items-center justify-center rounded-md border border-[#D1D5DB] px-4 text-14 font-600 text-[#3D3D3D] hover:bg-[#F5F6F7]"
                    onClick={onClose}
                  >
                    {translate("btn-cancel")}
                  </button>
                  <button
                    type="button"
                    className="flex h-10 items-center justify-center rounded-md border border-[#011BB6] bg-[#011BB6] px-4 text-14 font-600 text-white hover:bg-[#00169A]"
                    onClick={onApply}
                  >
                    {translate("confirm")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const LanguageGroup = ({
  className = "",
  showCurrency = true,
  onLanguageChange,
  onCurrencyChange,
}: LanguageGroupProps) => {
  const { translate, changeLocale } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageCode>(getStoredLanguage);
  const [draftLanguage, setDraftLanguage] =
    useState<LanguageCode>(selectedLanguage);
  const [selectedCurrency, setSelectedCurrency] =
    useState<CurrencyCode>(getStoredCurrency);
  const [draftCurrency, setDraftCurrency] =
    useState<CurrencyCode>(selectedCurrency);
  const [open, setOpen] = useState(false);
  const selectedLanguageOption =
    LANGUAGE_OPTIONS.find((language) => language.code === selectedLanguage) ??
    LANGUAGE_OPTIONS[0];

  const handleOpen = () => {
    setDraftLanguage(selectedLanguage);
    setDraftCurrency(selectedCurrency);
    setOpen(true);
  };

  const handleApply = () => {
    const shouldReload = showCurrency && draftCurrency !== selectedCurrency;

    localStorage.setItem("lang", draftLanguage);
    localStorage.setItem("wsCurrency", draftCurrency);
    setSelectedLanguage(draftLanguage);
    setSelectedCurrency(draftCurrency);
    changeLocale(draftLanguage);
    onLanguageChange?.(draftLanguage);
    onCurrencyChange?.(draftCurrency);
    window.dispatchEvent(
      new CustomEvent("workspaceCurrencyChange", {
        detail: { currency: draftCurrency },
      })
    );
    setOpen(false);

    if (shouldReload) {
      window.location.reload();
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        className="flex h-10 items-center gap-x-2 rounded-md border border-[#E5E7EB] bg-white px-3 text-[#3D3D3D] shadow-sm transition-colors hover:border-[#C5CCD2] hover:bg-[#F5F6F7]"
        onClick={handleOpen}
      >
        <img
          src={selectedLanguageOption.icon}
          className="size-[20px] rounded-full"
          alt={selectedLanguageOption.name}
        />
        <span className="text-14 font-700">
          {selectedLanguageOption.shortName}
        </span>
        {showCurrency && (
          <>
            <span className="h-4 w-px bg-[#D1D5DB]" />
            <span className="text-14 font-700 uppercase">
              {selectedCurrency}
            </span>
          </>
        )}
        <ChevronDownIcon className="size-[16px]" strokeWidth={3} />
      </button>

      <DialogLanguage
        open={open}
        showCurrency={showCurrency}
        draftCurrency={draftCurrency}
        draftLanguage={draftLanguage}
        onClose={() => setOpen(false)}
        onApply={handleApply}
        setDraftCurrency={setDraftCurrency}
        setDraftLanguage={setDraftLanguage}
        translate={translate}
      />
    </div>
  );
};

export default LanguageGroup;
